import moment from 'moment/moment';

// Default date format to be used in the project
export const getDateFormat = (date) => {
    if (date?.length < 1) {
        return '';
    }

    return moment(date).format('[As at] dddd, Do MMM YYYY');
};

// format number to 2 decimals
export const formatNumber = (number, decimals = 2) => {
    if (isNumber(number) === false) {
        return number;
    }

    return thousandSeparator(number.toFixed(decimals));
};

export const convertThousandToNumber = (input) => {
    if (input === undefined) {
        return;
    }

    if (typeof input === 'number') {
        return input;
    }

    return Number(input.replace(/,/g, ''));
};

/**
 * Format number to a minimum number of decimal places and up to the maximum
 */
export const formatNumberWithinPrecisionRange = (number, minPrecision, maxPrecision) => {
    if (isNumber(number) === false) {
        return number;
    }

    const numberParts = number.toString().split('.');
    const decimals = numberParts[1];
    const decimalCount = decimals === undefined ? 0 : decimals.length;

    if (decimalCount <= minPrecision) {
        return formatNumber(number, minPrecision);
    }

    if (decimalCount > maxPrecision) {
        return formatNumber(number, maxPrecision);
    }

    return formatNumber(number, decimalCount);
}

export const thousandSeparator = (number) => {
    const numberAsString = isNumber(number) ? number.toString() : number;

    if (typeof numberAsString !== 'string') {
        return number;
    }

    const decimalSeparator = '.';

    let numberParts = numberAsString.split(decimalSeparator);
    // only add thousand separators for integer digits
    // negative look-behind not available in ie11 and safari
    // e.g. /\B(?<!\.\d*)(?=(\d{3})+(?!\d)) ref: https://stackoverflow.com/a/2901298
    numberParts[0] = numberParts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return numberParts.join(decimalSeparator);
}

export const isNumber = (number) => {
    // additional check for NaN as it's type is returned as a number
    return typeof number === 'number' && isNaN(number) === false;
}

export const truncateNumber = (num, decimals, truncateThreshold = '>= 1000000') => {
    const number = Number(String(num).replace(/,/g, ''));

    const truncateMap = {
        'T': 1000000000000,
        'B': 1000000000,
        'M': 1000000,
        'K': 1000,
    };

    let precision = decimals;

    const threshold = parseFloat(truncateThreshold.replace('>= ', ''));

    if (isNaN(number)) {
        console.warn(`Cannot truncate value: ${num}`);
        return num;
    }

    if (isNaN(threshold)) {
        console.warn('Truncate threshold must be a number (with it without the ">=" prefix)');
        return num;
    }

    for (var key in truncateMap) {
        // Truncate any value above the threshold
        if (number >= truncateMap[key] && truncateMap[key] >= threshold) {
            // Format to e.g 9,999,999,999.00
            return formatNumber(number / truncateMap[key], decimals) + key;
        }
    }

    if (number > -1 && number < 1) {
        // Small numbers are given 3 decimals
        precision = 3;
    }

    return formatNumber(number, precision);
};

export const truncateAssetNumber = (num, decimals = 2, threshold = 100000000) => {
    const number = Number(String(num).replace(/,/g, ''));

    const truncateMap = {
        'T': 1000000000000,
        'B': 1000000000,
        'M': 1000000
    };

    if (isNaN(number)) {
        console.warn(`Cannot truncate value: ${num}`);
        return num;
    }

    for (const key in truncateMap) {
        // Truncate any value above the threshold
        if (number >= threshold && number >= truncateMap[key]) {
            // Format to e.g 9,999,999,999.00
            return formatNumber(number / truncateMap[key], decimals) + key;
        }
    }

    return formatNumber(number, decimals);
};

export const getDecimals = (number) => {
    if (number == null) {
        return null;
    }

    const numberAsString = typeof number === 'string' ? number : number.toString();
    return numberAsString.split('.')[1];
}

// Encode url as lowercase
export const urlEncode = (url) => `${encodeURI(url.replace('.', '-').toLowerCase())}`;

// Encode url as user passes it
export const urlEncodeStandard = (url) => `${encodeURI(url.replace('.', '-'))}`;

/**
 * Create an object where each property is a sum of the same property of each
 * object within an array. Only outputs numbers
 *
 * @param array
 * @param properties
 */
export const rollup = (array, properties) => {
    const output = {};

    properties.forEach((property) => {
        output[property] = 0;
    });

    array.forEach((object) => properties.forEach((property) => {
        if (isNaN(object[property])) {
            return;
        }

        output[property] += object[property];
    }));

    return output;
};
